import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customer Mailer tab — the customer's configured mailers (`/customers/:id/mailers`)
 * plus the delivered email history (`/customers/:id/mailers/tasks`). Field names
 * are the v4 backend verbatim (verified live).
 */

/** `GET /customers/:id/mailers` row — a configured mailer (recipients + types). */
export interface CustomerMailerRow {
  customer_mailer_id: number;
  customer_id: number;
  customer_mailer_name: string;
  mailer_type_ids?: number[];
  mailer_receiver?: string | null;
  mailer_cc?: string | null;
  mailer_bcc?: string | null;
  service_ids?: number[] | null;
  sendable?: boolean;
  mailer_types?: { mailer_type_id?: number; mailer_type_name?: string; [key: string]: unknown }[];
  services?: unknown[];
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

/** `GET /customers/:id/mailers/tasks` row — one delivered/scheduled email. */
export interface CustomerMailerTaskRow {
  mailer_id: number;
  mailer_subject?: string;
  mailer_status?: string;
  mailer_receiver?: string[];
  mailer_cc?: string[];
  mailer_bcc?: string[];
  mailer_content?: string;
  mailer_sender?: string;
  mailer_send_at?: string | null;
  mailer_performed_at?: string | null;
  mailer_received_at?: string | null;
  mailer_bounced_at?: string | null;
  receipt_status?: string;
  mailer_referrer_type?: string;
  mailer_referrer_id?: string;
  mailer_template_id?: number | null;
  mailer_created_at?: string;
  metadata?: Record<string, unknown>;
}

export interface MailerTasksParams {
  page?: number;
  page_size?: number;
}

/** `GET /customers/:id/mailers` — configured mailers (flat array). */
export function useCustomerMailers(customerId: number) {
  return useQuery({
    queryKey: ['customers', customerId, 'mailers'],
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseArray<CustomerMailerRow>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerMailerRow>>(
        `/customers/${customerId}/mailers`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/:id/mailers/tasks` — delivered email history (paginated). */
export function useCustomerMailerTasks(customerId: number, params: MailerTasksParams = {}) {
  return useQuery({
    queryKey: ['customers', customerId, 'mailers', 'tasks', params],
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseArray<CustomerMailerTaskRow>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerMailerTaskRow>>(
        `/customers/${customerId}/mailers/tasks?${qs.stringify({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ─────────────────────────────────────────────────────────── */

/**
 * Payload for creating or updating a customer mailer. Field names mirror the
 * Go `CreateCustomerMailerParams` / `UpdateCustomerMailerParams` structs verbatim.
 */
export interface CustomerMailerPayload {
  customer_mailer_name: string;
  mailer_type_ids: number[];
  mailer_receiver: string;
  mailer_cc?: string;
  mailer_bcc?: string;
  service_ids?: number[];
  /** Only relevant on update. */
  sendable?: boolean;
}

const mailerKeys = {
  list: (customerId: number) => ['customers', customerId, 'mailers'] as const,
};

/** `POST /customers/:id/mailers` — create a new mailer configuration. */
export function useCreateCustomerMailer(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CustomerMailerPayload): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/customers/${customerId}/mailers`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mailerKeys.list(customerId) });
    },
  });
}

/** `PUT /customers/:id/mailers/:mailerId` — update an existing mailer configuration. */
export function useUpdateCustomerMailer(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      mailerId: number;
      payload: CustomerMailerPayload;
    }): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${customerId}/mailers/${vars.mailerId}`,
        vars.payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mailerKeys.list(customerId) });
    },
  });
}
